import { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { t } from '../i18n'

const CartContext = createContext()

const initialState = {
  items: [],
  total: 0,
  itemCount: 0,
  branchId: null,
  tableNumber: null
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(
        item => item.menuItemId === action.payload.menuItemId && 
        JSON.stringify(item.modifiers) === JSON.stringify(action.payload.modifiers) &&
        item.variantId === action.payload.variantId && item.note === action.payload.note
      )

      if (existingItem) {
        const newQuantity = existingItem.quantity + action.payload.quantity;
        const newTotal = existingItem.unitPrice * newQuantity;
        const quantityDiff = action.payload.quantity;

        return {
          ...state,
          items: state.items.map(item =>
            item.id === existingItem.id
              ? { 
                  ...item, 
                  quantity: newQuantity,
                  total: newTotal
                }
              : item
          ),
          total: state.total + (action.payload.unitPrice * quantityDiff),
          itemCount: state.itemCount + quantityDiff
        }
      } else {
        return {
          ...state,
          items: [...state.items, { 
            ...action.payload, 
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9) 
          }],
          total: state.total + action.payload.total,
          itemCount: state.itemCount + action.payload.quantity
        }
      }

    case 'REMOVE_ITEM':
      const itemToRemove = state.items.find(item => item.id === action.payload)
      if (!itemToRemove) return state

      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
        total: state.total - itemToRemove.total,
        itemCount: state.itemCount - itemToRemove.quantity
      }

    case 'UPDATE_QUANTITY':
      const itemToUpdate = state.items.find(item => item.id === action.payload.id)
      if (!itemToUpdate) return state

      const quantityDiff = action.payload.quantity - itemToUpdate.quantity
      const priceDiff = quantityDiff * itemToUpdate.unitPrice
      const newTotal = itemToUpdate.unitPrice * action.payload.quantity

      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { 
                ...item, 
                quantity: action.payload.quantity, 
                total: newTotal 
              }
            : item
        ),
        total: state.total + priceDiff,
        itemCount: state.itemCount + quantityDiff
      }

    case 'CLEAR_CART':
      return initialState

    case 'SET_CART':
      return action.payload
    
    case 'SET_BRANCH_INFO':
      if (state.branchId === action.payload.branchId && state.tableNumber === action.payload.tableNumber) return state
      return {
        ...(state.branchId && state.branchId !== action.payload.branchId ? initialState : state),
        branchId: action.payload.branchId,
        tableNumber: action.payload.tableNumber
      }

    default:
      return state
  }
}

export function CartProvider({ children }) {
  // Read the saved cart before the first render - loading it in an effect
  // raced the save effect below, which wrote an empty cart over it on reload.
  const [state, dispatch] = useReducer(cartReducer, initialState, (init) => {
    try {
      const saved = JSON.parse(localStorage.getItem('posq_cart') || 'null')
      return saved && Array.isArray(saved.items) ? { ...init, ...saved } : init
    } catch {
      return init
    }
  })
  // Slide-in cart drawer state (ecommerce storefront) - kept separate from
  // the reducer since it's pure UI state, not cart data to persist.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])

  // Save cart to localStorage
  useEffect(() => {
    try { localStorage.setItem('posq_cart', JSON.stringify(state)) } catch { /* storage unavailable */ }
  }, [state])

  const addItem = (menuItem, quantity = 1, modifiers = [], note = '', branchId = null, tableNumber = null, variant = null) => {
    // Sold out (stock ran out) or hidden by the store: never add to the cart.
    if (menuItem?.in_stock === false || menuItem?.is_available === false || menuItem?.is_available === 0) {
      toast.error(t('Sorry, "{name}" is out of stock', { name: menuItem.name }))
      return
    }
    const basePrice = parseFloat(menuItem.price || 0);
    const variantPrice = variant ? parseFloat(variant.price_adjustment || 0) : 0;
    const modifierTotal = modifiers.reduce((sum, modifier) => sum + parseFloat(modifier.extra_price || 0), 0);
    
    const unitPrice = basePrice + variantPrice + modifierTotal;
    const total = unitPrice * quantity;

    const cartItem = {
      menuItemId: menuItem.id,
      name: menuItem.name,
      image: menuItem.image || menuItem.images?.[0] || null,
      price: menuItem.price,
      quantity,
      unitPrice,
      total,
      modifiers,
      note,
      branchId: branchId || state.branchId,
      tableNumber: tableNumber || state.tableNumber,
      variantId: variant?.id || null,
      variantName: variant?.name || null,
      variantPriceAdjustment: variant?.price_adjustment || 0
    };

    dispatch({
      type: 'ADD_ITEM',
      payload: cartItem
    });
  };
  
  const setBranchInfo = useCallback((branchId, tableNumber) => {
    dispatch({ 
      type: 'SET_BRANCH_INFO', 
      payload: { branchId, tableNumber } 
    })
  }, [])

  const removeItem = (itemId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId })
  }

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId)
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: itemId, quantity } })
    }
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
  }

  const value = {
    ...state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setBranchInfo,
    isDrawerOpen,
    openDrawer,
    closeDrawer
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
